'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery, useQueries, type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { ScopeProvider } from 'jotai-scope';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/navigation';
import { parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useThrottledValue } from '~/components/hooks/throttling';
import { Button, Modal } from '~/components/primitives';
import { useActivitySearchCtx } from '~/features/activities/search/client/context';
import type { ActivitySearchPage } from '~/features/activities/search/server';
import { ACTIVITY_BOOST_AD_QUERY_PARAMS, ActivityFilterResults_calculatePagination, _ACTIVITY_SEARCH_QUERY_OPTIONS } from '~/features/activities/search/shared';
import { ACTIVITY_FILTER_RULES, type ActivityFilterRules } from '~/features/activities/filterRules';
import { usePortalCtx } from '~/features/global/portal';
import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import { useNavigationGuard, type NavigationGuardIntent } from '~/modules/next/useNavigationGuard';
import { useIsTailwindBreakpoint } from '~/modules/tailwind/hooks/useIsTailwindBreakpoint';
import { useTRPC, useTRPCClient } from '~/modules/trpc/client';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { action_bizz_updateActivitySelection } from '../actions';
import { Catalog_bizzSearchOptions, Catalog_toSearchCustomization } from '../_lib/catalog-search';
import { sortIds, type Catalog_EditMode_Rules } from '../_lib/rules';
export type { Catalog_EditMode_Rules };
type ExitIntent = NavigationGuardIntent | {
  kind: 'cog';
};

/** Wizard step in catalog edit mode. The URL `?editStep` is the single source
 * of truth: presence == edit mode on, value == which step is active. */
export type Catalog_EditMode_Step = 'rules' | 'selection';
type Catalog_EditMode_SessionContextValue = {
  /** Active wizard step, or `null` when not editing. A non-null `step` is the
   * single "is editing" signal: it's only set once `?editStep` is present AND
   * the viewport is at/above `lg` (edit mode is desktop-only). */
  step: Catalog_EditMode_Step | null;
  /** Move between wizard steps without leaving edit mode. */
  setStep: (step: Catalog_EditMode_Step) => void;
  /** Cog button entry: opens the wizard on step 1 (`?editStep=rules`). */
  enter: () => void;
  /** Cog button exit: dirty-aware — clears `?editStep` immediately if clean,
   * otherwise opens the Save / Discard / Cancel modal. */
  exit: () => void;
  rules: Catalog_EditMode_Rules;
  setRules: (updater: (prev: Catalog_EditMode_Rules) => Catalog_EditMode_Rules) => void;
  /** `true` when the draft rules differ from the committed (last-saved)
   * value — `!isEqual(normalize(draft), normalize(seed))`. `normalize` sorts
   * the ID arrays so the comparison is order-stable; `seed` tracks the
   * committed atom, so a successful save flips this back to `false`. */
  hasChanges: boolean;
  /** `true` while a save action is in flight. Surfaces to the footer button
   * and the exit modal so both reflect the same loading state. */
  isSaving: boolean;
  /** Reset state to the original snapshot. Stays in edit mode. */
  reset: () => void;
  /** Persist current rules to the server, dispatch a toast, and advance the
   * dirty baseline. Rethrows on failure so callers (exit modal) can keep
   * their UI open for retry. */
  save: () => Promise<void>;
  /** Save & publish then leave edit mode (clears `?editStep`). Wired to the
   * wizard footer and the sticky view-tab "Save & publish" buttons. On save
   * failure it stays in edit mode — `save` has already surfaced the error
   * toast — so the user can retry or discard. */
  saveAndExit: () => Promise<void>;
};
const Ctx = createContext<Catalog_EditMode_SessionContextValue | null>(null);
const NOOP = () => {};
const ASYNC_NOOP = async () => {};

/** Stable identity so re-rendering `Catalog_EditMode_Session` (e.g. after a save
 * updates the committed atom) never re-creates the edit scope / drops the
 * draft. */
const SCOPED_FILTER_ATOMS = [ACTIVITY_FILTER_RULES];

/** Owns catalog edit mode on /bizz/[slug]/booking. The rules live in the
 * `ACTIVITY_FILTER_RULES` jotai atom: in browse mode consumers read the
 * committed (BIZZ-portal) value hydrated by the bizz layout; entering edit
 * mounts a `ScopeProvider` that gives this subtree an isolated draft copy
 * (`Catalog_EditMode_EditScope`). Saving pushes the draft back to the portal
 * atom (+ server action); discarding/exiting just unmounts the scope. */
export const Catalog_EditMode_Session: React.FC<{
  bizzID: number;
  children: React.ReactNode;
}> = props => {
  const $ = _c(15);
  const isLg = useIsTailwindBreakpoint("lg");
  const [editStep, setEditStep] = useQueryState("editStep", EDIT_STEP_QP);
  const isEditing = editStep !== null && isLg;
  const committed = useAtomValue(ACTIVITY_FILTER_RULES);
  const commit = useSetAtom(ACTIVITY_FILTER_RULES);
  let t0;
  if ($[0] !== setEditStep) {
    t0 = () => setEditStep("rules");
    $[0] = setEditStep;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const enter = t0;
  let t1;
  if ($[2] !== committed) {
    t1 = normalize(committed);
    $[2] = committed;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== enter || $[5] !== t1) {
    t2 = {
      step: null,
      setStep: NOOP,
      enter,
      exit: NOOP,
      rules: t1,
      setRules: NOOP,
      hasChanges: false,
      isSaving: false,
      reset: NOOP,
      save: ASYNC_NOOP,
      saveAndExit: ASYNC_NOOP
    };
    $[4] = enter;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const browseValue = t2;
  if (!isEditing) {
    let t3;
    if ($[7] !== browseValue || $[8] !== props.children) {
      t3 = <Ctx.Provider value={browseValue}>{props.children}</Ctx.Provider>;
      $[7] = browseValue;
      $[8] = props.children;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t3;
  if ($[10] !== commit || $[11] !== committed || $[12] !== props.bizzID || $[13] !== props.children) {
    t3 = <ScopeProvider atoms={SCOPED_FILTER_ATOMS}><Catalog_EditMode_EditScope bizzID={props.bizzID} seed={committed} commit={commit}>{props.children}</Catalog_EditMode_EditScope></ScopeProvider>;
    $[10] = commit;
    $[11] = committed;
    $[12] = props.bizzID;
    $[13] = props.children;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};

/** The edit-mode subtree, rendered inside a `ScopeProvider` that isolates
 * `ACTIVITY_FILTER_RULES` to a draft copy. Seeds the draft from the committed
 * `seed`, tracks dirty against it, and on save pushes the draft to the portal
 * atom via `commit` (+ persists). Owns the exit interception (cog click,
 * capture-phase `<a>` clicks, `popstate`, `beforeunload`) and the save/discard
 * modal — all only relevant while editing, so they live here. */
const Catalog_EditMode_EditScope: React.FC<{
  bizzID: number;
  seed: ActivityFilterRules;
  commit: (rules: ActivityFilterRules) => void;
  children: React.ReactNode;
}> = props => {
  // Seed the scoped copy from the committed value (a freshly scoped atom
  // otherwise starts at its `NO_RULES` default).
  useHydrateAtoms([[ACTIVITY_FILTER_RULES, props.seed]]);
  const i18n = useI18n();
  const router = useRouter();
  const notificationCtx = useNotifications();
  // Shares the `editStep` URL state with the outer session via nuqs.
  const [editStep, setEditStep] = useQueryState('editStep', EDIT_STEP_QP);

  // Scoped copy: reads/writes here stay local to the edit subtree until saved.
  const draft = useAtomValue(ACTIVITY_FILTER_RULES);
  const setDraft = useSetAtom(ACTIVITY_FILTER_RULES);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingIntent, setPendingIntent] = useState<ExitIntent | null>(null);

  // Normalise both sides (sorted ID arrays, `max` nulled) so the dirty check
  // is a stable positional deep-equal. `seed` is the live committed value, so
  // after a successful save `hasChanges` flips back to `false` on its own.
  const rules = useMemo(() => normalize(draft), [draft]);
  const seedRules = useMemo(() => normalize(props.seed), [props.seed]);
  const hasChanges = !isEqual(rules, seedRules);
  const setRules = useCallback((updater: (prev: Catalog_EditMode_Rules) => Catalog_EditMode_Rules) => setDraft(prev => updater(normalize(prev))), [setDraft]);
  const reset = useCallback(() => setDraft(props.seed), [setDraft, props.seed]);
  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      await action_bizz_updateActivitySelection({
        bizzID: props.bizzID,
        originalGroupIDs: props.seed.activityFilters.groupIDs.IDs ?? null,
        data: rules
      });
      // Publish the draft to the BIZZ-portal atom so browse mode (and the
      // rest of the portal) reflects the new rules immediately.
      props.commit(rules);
      notificationCtx.dispatchSuccess({
        message: i18n._('bizz.booking.edit.save.success', undefined, {
          message: 'Successfully updated activity selection'
        })
      });
    } catch (error) {
      notificationCtx.dispatchError();
      // Rethrow so the exit-modal `Save & publish` button can keep the
      // modal open for retry.
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [props.bizzID, props.seed, props.commit, rules, notificationCtx, i18n]);
  const saveAndExit = useCallback(async () => {
    try {
      await save();
      setEditStep(null);
    } catch {
      // `save` already dispatched an error toast; stay in edit mode.
    }
  }, [save, setEditStep]);
  const performExit = useCallback((intent: ExitIntent) => {
    if (intent.kind === 'cog') {
      setEditStep(null);
      return;
    }
    if (intent.kind === 'link') {
      router.push(intent.href);
      return;
    }
    // popstate: the URL has already changed by the time we hear about
    // it; Next.js' router is already handling the new location.
  }, [router, setEditStep]);
  const tryExit = useCallback((intent_0: ExitIntent) => {
    if (!hasChanges) {
      performExit(intent_0);
      return;
    }
    setPendingIntent(intent_0);
  }, [hasChanges, performExit]);
  const enter = useCallback(() => setEditStep('rules'), [setEditStep]);
  const exit = useCallback(() => tryExit({
    kind: 'cog'
  }), [tryExit]);
  const setStep = useCallback((next: Catalog_EditMode_Step) => setEditStep(next), [setEditStep]);

  // Guards attach on mount (edit enter) and detach on unmount (edit exit).
  // `isBlocked` reads the live dirty flag; its identity only changes when
  // `hasChanges` flips, not per keystroke.
  useNavigationGuard({
    enabled: true,
    isBlocked: useCallback(() => hasChanges, [hasChanges]),
    onIntercept: useCallback((intent_1: NavigationGuardIntent) => setPendingIntent(intent_1), [])
  });
  const closeModal = useCallback(() => {
    if (isSaving) return;
    setPendingIntent(null);
  }, [isSaving]);
  const handleSaveFromModal = useCallback(async () => {
    if (!pendingIntent) return;
    try {
      await save();
      const intent_2 = pendingIntent;
      setPendingIntent(null);
      performExit(intent_2);
    } catch {
      // Notification toast already dispatched by `save`; keep the modal
      // open so the user can retry or pick Discard.
    }
  }, [pendingIntent, save, performExit]);
  const handleDiscardFromModal = useCallback(() => {
    if (!pendingIntent) return;
    reset();
    const intent_3 = pendingIntent;
    setPendingIntent(null);
    performExit(intent_3);
  }, [pendingIntent, reset, performExit]);
  const value = useMemo<Catalog_EditMode_SessionContextValue>(() => ({
    step: editStep,
    setStep,
    enter,
    exit,
    rules,
    setRules,
    hasChanges,
    isSaving,
    reset,
    save,
    saveAndExit
  }), [editStep, setStep, enter, exit, rules, setRules, hasChanges, isSaving, reset, save, saveAndExit]);
  return <Ctx.Provider value={value}>
            {props.children}
            {pendingIntent ? <Modal onClose={closeModal} className="max-w-[95vw] sm:max-w-lg">
                    <Modal.Header title={i18n._('bizz.booking.edit.exitModal.title', undefined, {
        message: 'Save your changes?'
      })} description={i18n._('bizz.booking.edit.exitModal.description', undefined, {
        message: 'You have unsaved changes to the catalog rules. Save them before leaving, or discard them to continue.'
      })} onClose={closeModal} />
                    <Modal.Footer className="!justify-between !pb-6 sm:!pb-10">
                        <Button variant="outline" size="md" iconBefore="trash" onClick={handleDiscardFromModal} disabled={isSaving}>
                            {i18n._('bizz.booking.edit.exitModal.discard', undefined, {
            message: 'Discard changes'
          })}
                        </Button>
                        <Button variant="tertiary" size="md" iconBefore="check" onClick={handleSaveFromModal} disabled={isSaving} isLoading={isSaving}>
                            {i18n._('bizz.booking.edit.savePublish', undefined, {
            message: 'Save & publish'
          })}
                        </Button>
                    </Modal.Footer>
                </Modal> : null}
        </Ctx.Provider>;
};
export function useCatalog_EditMode_Session() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useCatalog_EditMode_Session must be used inside <Catalog_EditMode_Session>");
  }
  return ctx;
}
function normalize(values: ActivityFilterRules): Catalog_EditMode_Rules {
  const filters = values.activityFilters;
  return {
    automaticallyAddNewActivitiesToSelection: values.automaticallyAddNewActivitiesToSelection,
    activityFilters: {
      rootCategoryIDs: sortIds(filters.rootCategoryIDs),
      activityRegionIDs: sortIds(filters.activityRegionIDs),
      budget: {
        min: filters.budget.min ?? null,
        max: filters.budget.max ?? null
      },
      participants: {
        min: filters.participants.min ?? null,
        max: filters.participants.max ?? null
      },
      groupIDs: {
        mode: filters.groupIDs.mode,
        IDs: sortIds(filters.groupIDs.IDs)
      }
    }
  };
}

// -----------------------------------------------------------------------------
// Catalog_EditView — read-side of the session
// -----------------------------------------------------------------------------

export type Catalog_EditMode_TabKey = 'visible' | 'selected' | 'all';
type Catalog_EditMode_TabCustomization = {
  customization: ActivitySearchPage.SearchCustomizationProps;
  /** True iff this tab is definitionally empty without firing a query — the
   * count badge renders `0` and the grid query short-circuits. */
  disabled: boolean;
};

/** Per-tab rules-derived customizations. File-private hook shared by the
 * tab-counts hook (size-1 badge queries) and the grid query (active tab in
 * edit mode). Reads `session.rules` + portal-level statics; output is stable
 * as long as those don't change. */
function useTabCustomizations() {
  const $ = _c(21);
  const session = useCatalog_EditMode_Session();
  const portal = usePortalCtx("BIZZ");
  const bizzTenantId = portal.bizz.tenantId;
  let t0;
  if ($[0] !== bizzTenantId || $[1] !== session.rules.activityFilters) {
    t0 = Catalog_toSearchCustomization(session.rules.activityFilters, bizzTenantId);
    $[0] = bizzTenantId;
    $[1] = session.rules.activityFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const visibleCustomization = t0;
  const t1 = !!visibleCustomization.disabled;
  let t2;
  if ($[3] !== t1 || $[4] !== visibleCustomization) {
    t2 = {
      disabled: t1,
      customization: visibleCustomization
    };
    $[3] = t1;
    $[4] = visibleCustomization;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const visible = t2;
  const selectedDisabled = (session.rules.activityFilters.groupIDs.IDs?.length ?? 0) === 0;
  const t3 = session.rules.activityFilters.groupIDs.IDs ?? undefined;
  let t4;
  if ($[6] !== bizzTenantId) {
    t4 = {
      tenantId: bizzTenantId
    };
    $[6] = bizzTenantId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== selectedDisabled || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      includeAds: false,
      weightSet: "control",
      disabled: selectedDisabled,
      groupIDs: t3,
      constraints: t4
    };
    $[8] = selectedDisabled;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== selectedDisabled || $[13] !== t5) {
    t6 = {
      disabled: selectedDisabled,
      customization: t5
    };
    $[12] = selectedDisabled;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const selected = t6;
  let t7;
  if ($[15] !== bizzTenantId) {
    t7 = {
      disabled: false,
      customization: {
        includeAds: false,
        weightSet: "control",
        constraints: {
          tenantId: bizzTenantId
        }
      }
    };
    $[15] = bizzTenantId;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const all = t7;
  let t8;
  if ($[17] !== all || $[18] !== selected || $[19] !== visible) {
    t8 = {
      visible,
      selected,
      all
    };
    $[17] = all;
    $[18] = selected;
    $[19] = visible;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/** Edit-mode tab badge counts. Runs three parallel `trpc.activities.search`
 * queries with `size: 1` — we only need `totalResults` for the badges. The
 * active tab's count query shares its cache key with the grid query, so
 * react-query dedupes — net 2 extra requests per edit-mode entry. */
export function useCatalog_EditMode_TabCounts() {
  const $ = _c(11);
  const session = useCatalog_EditMode_Session();
  const i18n = useI18n();
  const portal = usePortalCtx("BIZZ");
  const trpc = useTRPC();
  const tabCustomizations = useTabCustomizations();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["visible", "selected", "all"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== i18n || $[2] !== portal || $[3] !== session || $[4] !== tabCustomizations || $[5] !== trpc) {
    t1 = {
      queries: (t0 as const).map(key => {
        const tab = tabCustomizations[key];
        return trpc.activities.search.queryOptions(_ACTIVITY_SEARCH_QUERY_OPTIONS(Catalog_bizzSearchOptions({
          bizz: portal.bizz,
          locale: i18n.locale,
          customization: tab.customization
        }), {
          pagination: {
            pageIdx: 0,
            size: 1
          }
        }), {
          staleTime: Infinity,
          enabled: session.step === "selection" && !tab.disabled
        });
      })
    };
    $[1] = i18n;
    $[2] = portal;
    $[3] = session;
    $[4] = tabCustomizations;
    $[5] = trpc;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const results = useQueries(t1);
  const t2 = tabCustomizations.visible.disabled ? 0 : results[0]?.data?.pageInfo?.totalResults ?? null;
  const t3 = tabCustomizations.selected.disabled ? 0 : results[1]?.data?.pageInfo?.totalResults ?? null;
  const t4 = tabCustomizations.all.disabled ? 0 : results[2]?.data?.pageInfo?.totalResults ?? null;
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      counts: {
        visible: t2,
        selected: t3,
        all: t4
      }
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/** Single grid query for the BIZZ booking page. Mode-aware: in edit mode the
 * active tab's rules-derived customization drives the query; in browse mode
 * the customization comes from `ActivitySearchCtxProvider`. Filters (user-
 * picked categories/regions/...) merge on top in both modes. Threading the
 * result to header + grid keeps the data source explicit.
 *
 * Pages accumulate: the catalog scrolls instead of paging, so the raw result
 * is handed out alongside the flattened list for the virtualized grid. */
export function useCatalog_BookingGridQuery(): {
  activities: TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'] | undefined;
  isFetched: boolean;
  isFetching: boolean;
  totalResults: number | undefined;
  infiniteQueryResult: UseInfiniteQueryResult<InfiniteData<TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>>, Error>;
} {
  const session = useCatalog_EditMode_Session();
  const i18n = useI18n();
  const portal = usePortalCtx('BIZZ');
  const ctx = useActivitySearchCtx();
  const trpcClient = useTRPCClient();
  const filters = ctx.useFilters();
  const throttledFilters = useThrottledValue(filters.value, 1000, [filters.value]);
  const [BOOST_AD_QP] = useQueryStates(ACTIVITY_BOOST_AD_QUERY_PARAMS);
  const [view] = useQueryState('view', VIEW_TAB_QP);
  const tabCustomizations = useTabCustomizations();

  // Step 1 (`rules`) intentionally hides the view-tab strip — the grid
  // always shows the current catalog so admins see the live effect of
  // rule edits. The `view` URL param is honoured only in step 2 (`selection`).
  const activeView = session.step === 'rules' ? 'visible' : view;
  const customization = session.step !== null ? tabCustomizations[activeView].customization : ctx.customization;

  // Everything the result depends on, in one object so it can be both the
  // query key and the queryFn's only input. A new tab or filter is a new key,
  // which drops the accumulated pages and scrolls from the top again.
  const queryOptions = useMemo(() => ({
    bizz: portal.bizz,
    locale: i18n.locale,
    customization,
    filters: {
      ...BOOST_AD_QP,
      ...throttledFilters
    }
  }), [BOOST_AD_QP, customization, i18n.locale, portal.bizz, throttledFilters]);
  const infiniteQueryResult = useInfiniteQuery({
    queryKey: ['bizz', 'booking', 'catalog-infinite', queryOptions],
    queryFn: ({
      pageParam
    }) => trpcClient.activities.search.query(_ACTIVITY_SEARCH_QUERY_OPTIONS(Catalog_bizzSearchOptions({
      bizz: queryOptions.bizz,
      locale: queryOptions.locale,
      customization: queryOptions.customization
    }), {
      ...queryOptions.filters,
      pagination: ActivityFilterResults_calculatePagination('BIZZ', pageParam)
    })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => lastPageParam + 1 >= lastPage.pageInfo.totalPages ? undefined : lastPageParam + 1,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    enabled: !customization?.disabled
  });
  const activities = useMemo(() => infiniteQueryResult.data?.pages.flatMap(page => page.activities), [infiniteQueryResult.data]);
  return {
    activities,
    isFetched: infiniteQueryResult.isFetched,
    isFetching: infiniteQueryResult.isFetching,
    totalResults: customization?.disabled ? 0 : infiniteQueryResult.data?.pages[0]?.pageInfo.totalResults,
    infiniteQueryResult
  };
}

/** View-tab URL parser. Shared with `mode-switch.tsx` (which resets the tab
 * to `visible` after a mode switch) and the BIZZ-side view-tab strip in
 * `Catalog_EditMode_Sidebar`/page layout. Defined here because the session is
 * the central edit-mode module that owns the URL state contract. */
export const VIEW_TAB_QP = parseAsStringLiteral(['visible', 'selected', 'all'] as const).withDefault('visible').withOptions({
  shallow: true
});

/** Wizard-step URL parser. Replaces the previous `?edit=1` + `?pane=...` pair:
 * `?editStep` presence drives edit mode, its value drives which step is
 * active. `null` (absent) means browse mode. */
const EDIT_STEP_QP = parseAsStringLiteral(['rules', 'selection'] as const).withOptions({
  shallow: true
});